import React, { useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Divider from '@precis-digital/kurama/Divider';
import { styled } from '@precis-digital/kurama/utils';
import { type CSSObject } from '@precis-digital/kurama/theme';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { MarkdownChartToken } from './components/MarkdownChartToken';
import { MarkdownCopilotTable } from './components/MarkdownCopilotTable';
import { createMarkdownElementComponents } from './markdownComponents';
import { markdownSanitizeSchema } from './markdownSanitizeSchema';
import { MarkdownTable, MarkdownTableBody, MarkdownTableDataCell, MarkdownTableHead, MarkdownTableHeaderCell, MarkdownTableRow } from './components/MarkdownTable';
import { preprocessMarkdownText } from './preprocessMarkdownText';
import type { PluggableList } from 'unified';
import 'katex/dist/katex.min.css';

// Disable single-dollar math so currency like $100 is not parsed as LaTeX.
// Equations use $$...$$ (inline when on one line; display when multiline $$...$$).
// Single-dollar inline math ($O_i$, $\rightarrow$) is normalized to $$...$$ first.
// remark-math before remark-gfm so math is tokenized before GFM touches underscores.
const REMARK_MATH_OPTIONS = {
  singleDollarTextMath: false
} as const;
const DEFAULT_REMARK_PLUGINS: PluggableList = [[remarkMath, REMARK_MATH_OPTIONS], remarkGfm];
const REHYPE_KATEX_OPTIONS = {
  throwOnError: false,
  strict: false
} as const;
// Cast: rehype-katex's `this: Processor` uses a different unified instance under pnpm isolation.
const DEFAULT_REHYPE_PLUGINS = [[rehypeKatex, REHYPE_KATEX_OPTIONS]] as PluggableList;
/**
 * Parse untrusted HTML from Copilot preprocessing, strip XSS vectors, then render math.
 * Sanitize must run after `rehype-raw` and before `rehype-katex` so KaTeX output is not stripped.
 */
const DEFAULT_REHYPE_PLUGINS_WITH_RAW = [rehypeRaw, [rehypeSanitize, markdownSanitizeSchema], [rehypeKatex, REHYPE_KATEX_OPTIONS]] as PluggableList;

/** react-markdown components plus allowlisted Copilot custom elements callers may override. */
type MarkdownComponents = React.ComponentProps<typeof ReactMarkdown>['components'] & {
  'skill-pill'?: React.ComponentType<{
    'data-index'?: string;
  }>;
};
interface MarkdownProps extends Omit<React.ComponentProps<typeof ReactMarkdown>, 'components'> {
  components?: MarkdownComponents;
  variant?: 'compact' | 'standard';
  /** Opaque chart id → payload map; Copilot injects CopilotChartData values. */
  chartIdMap?: Map<string, unknown>;
  /**
   * When provided, tables are rewritten via this extractor (Copilot supplies
   * `extractMarkdownTables` and keeps the id map for `copilot-table` rendering).
   */
  extractTables?: (markdown: string) => {
    markdown: string;
    tableIdMap: Map<string, unknown>;
  };
  /**
   * Parse sanitized raw HTML even without `chartIdMap` / `extractTables`, for callers that
   * inject their own allowlisted custom elements (e.g. `skill-pill`) via `components`.
   */
  allowRawHtml?: boolean;
}
const StyledMarkdownContainer = styled('div')({
  wordWrap: 'break-word'
});
const Markdown = ({
  children,
  remarkPlugins,
  rehypePlugins,
  components,
  variant = 'standard',
  chartIdMap,
  extractTables,
  allowRawHtml = false,
  ...props
}: MarkdownProps): React.ReactElement => {
  const chartIdMapRef = useRef(chartIdMap);
  chartIdMapRef.current = chartIdMap;
  const tableIdMapRef = useRef<Map<string, unknown>>(new Map());
  const useChartCardTables = extractTables != null;
  const mergedComponents = useMemo(() => {
    const tableVariant = variant;
    const tableComponents = useChartCardTables ? {} : {
      table: ({
        node,
        ...tableProps
      }: {
        node?: unknown;
        children?: React.ReactNode;
      }) => <MarkdownTable node={node} {...tableProps} />,
      thead: ({
        node: node_0,
        ...headProps
      }: {
        node?: unknown;
        children?: React.ReactNode;
      }) => <MarkdownTableHead node={node_0} {...headProps} />,
      tbody: ({
        node: node_1,
        ...bodyProps
      }: {
        node?: unknown;
        children?: React.ReactNode;
      }) => <MarkdownTableBody node={node_1} {...bodyProps} />,
      tr: ({
        node: node_2,
        ...rowProps
      }: {
        node?: unknown;
        children?: React.ReactNode;
      }) => <MarkdownTableRow node={node_2} {...rowProps} />,
      th: ({
        node: node_3,
        align,
        ...headerProps
      }: {
        node?: unknown;
        align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
        children?: React.ReactNode;
      }) => <MarkdownTableHeaderCell node={node_3} align={align} variant={tableVariant} {...headerProps} />,
      td: ({
        node: node_4,
        align: align_0,
        ...dataProps
      }: {
        node?: unknown;
        align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
        children?: React.ReactNode;
      }) => <MarkdownTableDataCell node={node_4} align={align_0} variant={tableVariant} {...dataProps} />
    };
    const chartTokenComponent = {
      'chart-token': ({
        'data-id': id,
        'data-type': type
      }: {
        'data-id'?: string;
        'data-type'?: string;
      }) => <MarkdownChartToken data-id={id} data-type={type} chartIdMap={chartIdMapRef.current} />
    };
    const hrComponent = {
      hr: () => <Divider />
    };

    // Allowlisted for user-message skill pills; renders nothing unless the caller overrides it.
    const skillPillComponent = {
      'skill-pill': () => null
    };
    const elementComponents = createMarkdownElementComponents(tableVariant);
    const copilotTableComponent = useChartCardTables ? {
      'copilot-table': ({
        'data-id': id_0
      }: {
        'data-id'?: string;
      }) => <MarkdownCopilotTable data-id={id_0} tableIdMap={tableIdMapRef.current} variant={tableVariant} />
    } : {};
    if (variant === 'compact') {
      return {
        ...tableComponents,
        ...chartTokenComponent,
        ...hrComponent,
        ...skillPillComponent,
        ...elementComponents,
        ...copilotTableComponent,
        ...components,
        p: ({
          node: _node,
          className: _className,
          children: paragraphChildren,
          style: _style,
          ref: _ref,
          ...paragraphProps
        }) => {
          return <StyledParagraph {...paragraphProps}>{paragraphChildren}</StyledParagraph>;
        },
        li: ({
          node: _node_0,
          className: _className_0,
          children: listChildren,
          style: _style_0,
          ref: _ref_0,
          ...listProps
        }) => {
          return <StyledListItem {...listProps}>{listChildren}</StyledListItem>;
        },
        ul: ({
          node: _node_1,
          className: _className_1,
          children: listChildren_0,
          style: _style_1,
          ref: _ref_1,
          ...listProps_0
        }) => {
          return <StyledUnorderedList {...listProps_0}>{listChildren_0}</StyledUnorderedList>;
        },
        ol: ({
          node: _node_2,
          className: _className_2,
          children: listChildren_1,
          style: _style_2,
          ref: _ref_2,
          ...listProps_1
        }) => {
          return <StyledOrderedList {...listProps_1}>{listChildren_1}</StyledOrderedList>;
        }
      };
    }
    return {
      ...tableComponents,
      ...chartTokenComponent,
      ...hrComponent,
      ...skillPillComponent,
      ...elementComponents,
      ...copilotTableComponent,
      ...components
    };
  }, [components, useChartCardTables, variant]);
  const processedChildren = useMemo(() => {
    if (typeof children !== 'string') return children;
    const {
      markdown
    } = preprocessMarkdownText(children, {
      processCopilotInlineTokens: chartIdMap != null || extractTables != null,
      extractTables: extractTables != null ? text => {
        const extracted = extractTables(text);
        tableIdMapRef.current = extracted.tableIdMap;
        return extracted.markdown;
      } : undefined
    });
    if (extractTables == null) {
      tableIdMapRef.current = new Map();
    }
    return markdown;
  }, [children, chartIdMap, extractTables]);
  const resolvedRemarkPlugins = remarkPlugins ?? DEFAULT_REMARK_PLUGINS;
  const needsRawHtml = allowRawHtml || chartIdMap != null || useChartCardTables;
  const resolvedRehypePlugins = rehypePlugins ?? (needsRawHtml ? DEFAULT_REHYPE_PLUGINS_WITH_RAW : DEFAULT_REHYPE_PLUGINS);
  return <StyledMarkdownContainer>
      <ReactMarkdown {...props} remarkPlugins={resolvedRemarkPlugins} rehypePlugins={resolvedRehypePlugins} components={mergedComponents}>
        {processedChildren}
      </ReactMarkdown>
    </StyledMarkdownContainer>;
};
const StyledListItem = styled('li')(({
  theme
}): CSSObject => ({
  ...theme.typography.body3,
  marginBottom: '2px',
  '&:last-of-type': {
    marginBottom: 0
  }
}));
const StyledUnorderedList = styled('ul')(() => ({
  paddingInlineStart: '24px',
  marginBlockEnd: '10px',
  marginBlockStart: 0,
  '&:last-child': {
    marginBlockEnd: 0
  }
}));
const StyledOrderedList = styled('ol')(() => ({
  paddingInlineStart: '24px',
  marginBlockEnd: '10px',
  marginBlockStart: 0,
  '&:last-child': {
    marginBlockEnd: 0
  }
}));
const StyledParagraph = styled('p')(({
  theme
}): CSSObject => ({
  ...theme.typography.body3,
  marginBlockEnd: '10px',
  marginBlockStart: 0,
  '&:last-child': {
    marginBlockEnd: 0
  }
}));
export default Markdown;